import { memo, useMemo } from 'react';
import { isEmpty } from 'lodash';
import {
  Typography,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Link
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp, PictureAsPdf, ArrowRight } from '@mui/icons-material';

import CommonNameList from './CommonNameList';
import Documents from './Documents';

import { regionSourceMapping, qcStatusList } from '../constants';

const ProductsCuration = ({
  activeIngredient,
  allCommonNames,
  addNewCommonName,
  removeCommonName,
  handleInputChange,
  onValidate,
  handleSubmit,
  handleProductQcStatusChange,
  updateOpenedLists,
  openedLists,
  currentSelectedProduct,
  setCurrentSelectedProduct,
  allProductDocuments,
  activeIngredientTrials,
  handleTrialIdChange
}: any) => {
  const ids = useMemo(() => {
    if (isEmpty(activeIngredientTrials)) {
      return [];
    }

    const uniqueIds = new Set();

    Object.keys(activeIngredientTrials).forEach((key: any) => {
      activeIngredientTrials[key].forEach((trial: any) => {
        uniqueIds.add(trial?.nct_id || trial?.euct_id || trial?.id || '');
      });
    });

    return Array.from(uniqueIds);
  }, [activeIngredientTrials]);

  return (
    <Stack direction='row' mt={2} justifyContent='space-between' gap={2}>
      <Stack
        gap={5}
        sx={{
          overflowY: 'auto',
          height: '72vh',
          pr: 3
        }}>
        <Stack gap={5}>
          {activeIngredient?.products?.map((product: any) => (
            <Stack key={product.application_number}>
              <Stack gap={2}>
                <Stack direction='row' spacing={1} alignItems='flex-start'>
                  <Stack
                    onClick={() => updateOpenedLists(product.application_number)}
                    sx={{
                      cursor: 'pointer'
                    }}>
                    {openedLists[product.application_number] ? <ArrowDropUp /> : <ArrowDropDown />}
                  </Stack>
                  <Stack>
                    <Stack direction='row' gap={1}>
                      <Typography fontWeight='bold' fontSize={18}>
                        Product Number:
                      </Typography>
                      <Typography fontSize={18}>
                        <Link
                          href={`https://www.ria.vivpro.ai/regulatory360/core/${
                            product.application_number
                          }/${regionSourceMapping[activeIngredient?.source]}`}
                          target='_blank'
                          rel='noopener'>
                          {product.application_number}
                        </Link>
                      </Typography>
                    </Stack>
                    <Stack direction='row' gap={1}>
                      <Typography fontSize={18} fontWeight='bold'>
                        Overlay Trials:
                      </Typography>
                      <Typography fontSize={18}>{product.overlay_trials_count}</Typography>
                    </Stack>
                  </Stack>
                  <Stack
                    direction='row'
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (!openedLists[product.application_number]) {
                        updateOpenedLists(product.application_number);
                      }

                      setCurrentSelectedProduct(product.application_number);
                    }}>
                    <Tooltip title='View Documents'>
                      <PictureAsPdf color='primary' />
                    </Tooltip>
                    {product.application_number === currentSelectedProduct && <ArrowRight />}
                  </Stack>
                </Stack>
                {openedLists[product.application_number] && (
                  <Stack gap={2} alignItems='center'>
                    <CommonNameList
                      commonNames={allCommonNames[product.application_number] || []}
                      addNewCommonName={addNewCommonName}
                      removeCommonName={removeCommonName}
                      handleInputChange={handleInputChange}
                      mainKey={product.application_number}
                      type='Overlay'
                      onValidate={onValidate}
                      ids={ids}
                      handleTrialIdChange={handleTrialIdChange}
                    />
                    <Stack gap={2}>
                      <FormControl size='small' sx={{ width: 250 }}>
                        <InputLabel>QC Status</InputLabel>
                        <Select
                          value={product?.qc_status}
                          label='QC Status'
                          onChange={event =>
                            handleProductQcStatusChange(event, product.application_number)
                          }>
                          {qcStatusList.map(qcStatus => (
                            <MenuItem key={qcStatus} value={qcStatus}>
                              {qcStatus}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant='contained'
                        size='small'
                        onClick={event =>
                          handleSubmit(event, product.application_number, 'application')
                        }>
                        Submit
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Stack flex={1}>
        {allProductDocuments[currentSelectedProduct] && (
          <Stack maxWidth='50vw' pr={1}>
            <Documents documentList={allProductDocuments[currentSelectedProduct]} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default memo(ProductsCuration);
